/**
 * SMART Session Management
 * Handles storage and retrieval of SMART on FHIR session data
 */

export interface SmartSession {
  accessToken: string;
  tokenType: string;
  expiresAt: number;
  scope: string;
  patient?: string;
  fhirServer: string;
  fhirUser?: string; // FHIR resource reference for the current user (e.g., "Practitioner/123")
  userType?: string; // Type of user resource (Practitioner, Patient, RelatedPerson, etc.)
  userId?: string; // ID extracted from fhirUser
}

const SESSION_KEY = 'smart-session';
const CODE_VERIFIER_KEY = 'smart-code-verifier';
const STATE_KEY = 'smart-oauth-state';
const METADATA_KEY = 'smart-fhir-metadata';

/**
 * Save SMART session to sessionStorage
 */
export function saveSession(session: SmartSession): void {
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
}

/**
 * Retrieve SMART session from sessionStorage
 * @returns Session object or null if not found or expired
 */
export function getSession(): SmartSession | null {
  const stored = sessionStorage.getItem(SESSION_KEY);
  if (!stored) return null;

  try {
    const session: SmartSession = JSON.parse(stored);
    
    // Check if token is expired
    if (session.expiresAt && Date.now() >= session.expiresAt) {
      clearSession();
      return null;
    }

    return session;
  } catch (error) {
    console.error('Error parsing session:', error);
    return null;
  }
}

/**
 * Clear SMART session from sessionStorage
 */
export function clearSession(): void {
  sessionStorage.removeItem(SESSION_KEY);
  sessionStorage.removeItem(CODE_VERIFIER_KEY);
  sessionStorage.removeItem(STATE_KEY);
  sessionStorage.removeItem(METADATA_KEY);
}

/**
 * Save PKCE code verifier
 */
export function saveCodeVerifier(verifier: string): void {
  sessionStorage.setItem(CODE_VERIFIER_KEY, verifier);
}

/**
 * Retrieve PKCE code verifier
 */
export function getCodeVerifier(): string | null {
  return sessionStorage.getItem(CODE_VERIFIER_KEY);
}

/**
 * Save OAuth state parameter
 */
export function saveState(state: string): void {
  sessionStorage.setItem(STATE_KEY, state);
}

/**
 * Retrieve and verify OAuth state parameter
 */
export function getState(): string | null {
  return sessionStorage.getItem(STATE_KEY);
}

/**
 * Save FHIR server metadata
 */
export function saveMetadata(metadata: any): void {
  sessionStorage.setItem(METADATA_KEY, JSON.stringify(metadata));
}

/**
 * Retrieve FHIR server metadata
 */
export function getMetadata(): any | null {
  const stored = sessionStorage.getItem(METADATA_KEY);
  if (!stored) return null;

  try {
    return JSON.parse(stored);
  } catch (error) {
    console.error('Error parsing metadata:', error);
    return null;
  }
}

/**
 * Check if user is authenticated with SMART
 */
export function isAuthenticated(): boolean {
  return getSession() !== null;
}

